import React from 'react';
import ArrayBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ArrayBuilder';
import { Attachment, ModelInputFileAttachmentType } from '@scale/shared/types/chat';
import { Button, Typography } from '@scale/scaleui';
import { BuilderInputTypes, ObjectSchema } from '@scale/shared/taskTemplate/types';
import type { TypeBuilderMapType } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemasType';

const PromptAttachmentSchema: ObjectSchema = {
  name: {
    displayName: 'Name',
    required: true,
    inputType: BuilderInputTypes.String,
    hint: 'The name of the attachment',
  },
  mimeType: {
    displayName: 'Mime type',
    required: false,
    inputType: BuilderInputTypes.String,
    hint: 'The mime type (e.g. image/jpg) of the attachment',
  },
  fileType: {
    displayName: 'Caption',
    required: false,
    inputType: BuilderInputTypes.ModelInputFileAttachmentChoicesBuilder,
    hint: 'One of image, text, json or audio',
  },
  url: {
    displayName: 'URL',
    required: false,
    inputType: BuilderInputTypes.String,
    hint: 'The URL of the attachment',
  },
};

const AttachmentsTypeSchemaMap: Record<ModelInputFileAttachmentType, ObjectSchema> = {
  [ModelInputFileAttachmentType.Image]: PromptAttachmentSchema,
  [ModelInputFileAttachmentType.Text]: PromptAttachmentSchema,
  [ModelInputFileAttachmentType.Json]: PromptAttachmentSchema,
  [ModelInputFileAttachmentType.Audio]: PromptAttachmentSchema,
  [ModelInputFileAttachmentType.Pdf]: PromptAttachmentSchema,
  [ModelInputFileAttachmentType.Document]: PromptAttachmentSchema,
  [ModelInputFileAttachmentType.Zip]: PromptAttachmentSchema,
};

interface PromptAttachmentsBuilderProps {
  name: string;
  existingValue?: Array<Attachment>;
  updateProperty: (arg: Array<Attachment>) => void;
  required?: boolean;
  hint?: string;
  typeBuilderMap: TypeBuilderMapType;
}

const PromptAttachmentsBuilder: React.FC<PromptAttachmentsBuilderProps> = ({
  name,
  existingValue = [],
  updateProperty,
  typeBuilderMap,
}) => {
  const providedAttachments = existingValue;

  const addProvidedAttachmentsResponses = () => {
    const newProvidedAttachmentsResponses = [...providedAttachments];
    // TODO (victor): add the rest of attachment values
    newProvidedAttachmentsResponses.push({
      fileType: ModelInputFileAttachmentType.Image,
      name: 'Replace with something descriptive',
    } as Attachment);
    updateProperty(newProvidedAttachmentsResponses);
  };

  return (
    <div className="flex flex-col gap-1">
      {name && <Typography.Subtitle3>{name}</Typography.Subtitle3>}
      <ArrayBuilder
        typeBuilderMap={typeBuilderMap}
        elements={providedAttachments}
        setElements={updateProperty}
        getKey={attachmentResponse => attachmentResponse.fileType}
        getTitle={attachmentResponse => attachmentResponse.fileType}
        getSchema={attachmentResponse => AttachmentsTypeSchemaMap[attachmentResponse.fileType]}
      />
      <div>
        <Button size="sm" onClick={() => addProvidedAttachmentsResponses()} variant="outline">
          Add Attachment
        </Button>
      </div>
    </div>
  );
};

export default PromptAttachmentsBuilder;
